/**
 * Hook interno del módulo CallCenterActiveCallRightColumn — carga el contexto
 * de pedido (COD o ecommerce) asociado a la llamada activa.
 *
 * Extraído del componente padre para bajar la complejidad ciclomática de ~64
 * a un nivel manejable. NO usar fuera del componente derecho de sesión activa.
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mapCodV2Row } from '@/lib/operations/codFromOrdersV2';
import { ROUTES } from '@/config/routes.config';
import type { WebrtcCallUiPhase } from '@/lib/telnyxCallState';

export interface OrderContext {
  kind: 'cod' | 'ecommerce';
  label: string;
  detail: string;
  link?: string;
  /** Nombre resuelto desde pedido (evita depender solo del split del `detail`). */
  nameHint?: string | null;
  /** Teléfono del pedido si el store WebRTC aún no tiene `remoteDialNumber`. */
  phoneFromOrder?: string | null;
}

interface CallRow {
  cod_order_id?: string | null;
  ecommerce_order_id?: string | null;
  destination_phone?: string | null;
}

interface CodOrderRow {
  first_name: string | null;
  last_name: string | null;
  whatsapp: string | null;
  product_name: string | null;
  total: number | null;
}

interface EcomOrderRow {
  customer_name?: string | null;
  customer_phone?: string | null;
  order_number?: number | null;
}

async function fetchCallRow(
  tenantId: string,
  effectiveCrmCallId: string | null,
): Promise<CallRow | null> {
  if (effectiveCrmCallId) {
    const { data, error } = await supabase
      .from('crm_calls')
      .select('cod_order_id, ecommerce_order_id, destination_phone')
      .eq('tenant_id', tenantId)
      .eq('id', effectiveCrmCallId)
      .maybeSingle();
    if (error || !data) return null;
    return data as CallRow;
  }
  // Fallback: llamada activa más reciente del tenant (últimos 3 min).
  const since = new Date(Date.now() - 180_000).toISOString();
  const { data, error } = await supabase
    .from('crm_calls')
    .select('cod_order_id, ecommerce_order_id, destination_phone')
    .eq('tenant_id', tenantId)
    .in('call_status', ['pending', 'initiated', 'ringing', 'answered'])
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return data as CallRow;
}

async function buildCodContext(
  tenantId: string,
  codId: string,
  destPhone: string | null,
): Promise<OrderContext | null> {
  // Dominio único orders_v2: nombre en order_delivery_profile, producto en la primera order_line, teléfono = customer_phone.
  const { data: codV2, error } = await supabase
    .from('orders_v2')
    .select('grand_total, customer_phone, order_delivery_profile(first_name, last_name), order_lines(name)')
    .eq('tenant_id', tenantId)
    .eq('id', codId)
    .maybeSingle();
  const cod = codV2 ? mapCodV2Row(codV2) : null;

  if (error || !cod) {
    // RLS o réplica impide leer el pedido → mostrar lo que ya tenemos en crm_calls.
    if (!destPhone) return null;
    return {
      kind: 'cod',
      label: 'Pedido formulario COD',
      detail: `Tel. marcado: ${destPhone}`,
      link: ROUTES.OPS_ORDER_DETAIL.replace(':id', codId),
      nameHint: 'Cliente formulario COD',
      phoneFromOrder: destPhone,
    };
  }

  const c = cod as CodOrderRow;
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || 'Cliente COD';
  const prod = c.product_name?.trim() || 'Pedido COD';
  const total =
    c.total != null && Number.isFinite(Number(c.total))
      ? ` · S/ ${Number(c.total).toFixed(2)}`
      : '';
  const wa = c.whatsapp?.trim() || null;
  return {
    kind: 'cod',
    label: 'Pedido formulario COD',
    detail: wa ? `${name} — ${prod}${total} · ${wa}` : `${name} — ${prod}${total}`,
    link: ROUTES.OPS_ORDER_DETAIL.replace(':id', codId),
    nameHint: name,
    phoneFromOrder: wa || destPhone,
  };
}

async function buildEcomContext(
  tenantId: string,
  ecomId: string,
  destPhone: string | null,
): Promise<OrderContext> {
  const { data: ord, error } = await supabase
    .from('orders_v2')
    .select('customer_name, customer_phone, order_number')
    .eq('tenant_id', tenantId)
    .eq('id', ecomId)
    .maybeSingle();

  if (error || !ord) {
    return {
      kind: 'ecommerce',
      label: 'Pedido tienda online',
      detail: `Referencia ${ecomId.slice(0, 8)}…`,
      link: ROUTES.ORDERS_ONLINE,
      nameHint: null,
      phoneFromOrder: destPhone,
    };
  }

  const o = ord as unknown as EcomOrderRow;
  const num =
    o.order_number != null
      ? String(o.order_number)
      : ecomId.slice(0, 8).toUpperCase();
  const name = o.customer_name?.trim() || 'Cliente tienda';
  const phone = o.customer_phone?.trim();
  return {
    kind: 'ecommerce',
    label: 'Pedido tienda online',
    detail: phone ? `${name} · ${phone} · ${num}` : `${name} · ${num}`,
    link: ROUTES.ORDERS_ONLINE,
    nameHint: name,
    phoneFromOrder: phone || null,
  };
}

export function useCallCenterOrderContext(
  tenantId: string,
  crmCallId: string | null,
  crmCallStatus: string | null,
  callPhase: WebrtcCallUiPhase,
): OrderContext | null {
  const [orderContext, setOrderContext] = useState<OrderContext | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setOrderContext(null);
      return;
    }
    const effectiveCrmCallId = crmCallId?.trim() || null;
    if (!effectiveCrmCallId && callPhase === 'none') {
      setOrderContext(null);
      return;
    }
    let cancelled = false;

    (async () => {
      const callRow = await fetchCallRow(tenantId, effectiveCrmCallId);
      if (cancelled) return;
      if (!callRow) {
        setOrderContext(null);
        return;
      }
      const destPhone = callRow.destination_phone?.trim() || null;
      if (callRow.cod_order_id) {
        const ctx = await buildCodContext(tenantId, callRow.cod_order_id, destPhone);
        if (!cancelled) setOrderContext(ctx);
        return;
      }
      if (callRow.ecommerce_order_id) {
        const ctx = await buildEcomContext(tenantId, callRow.ecommerce_order_id, destPhone);
        if (!cancelled) setOrderContext(ctx);
        return;
      }
      setOrderContext(null);
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantId, crmCallId, crmCallStatus, callPhase]);

  return orderContext;
}
